import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllAssessments,
  getAssessmentById,
  createAssessment,
  updateAssessment,
  deleteAssessment
} from '../services/assessmentsService';
import Notification from '../components/Common/Notification';

export const AssessmentContext = createContext();

export const AssessmentProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllAssessments = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllAssessments(authHeaders);
      setAssessments(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching assessments.';
      showNotification(errorMessage, true);
    }
  };

  const fetchAssessmentById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAssessmentById(id, authHeaders);
      setSelectedAssessment(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching assessment with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addAssessment = async (assessmentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newAssessment = await createAssessment(assessmentData, authHeaders);
      setAssessments(prevAssessments => [...prevAssessments, newAssessment]);
      showNotification('Assessment added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding assessment.';
      showNotification(errorMessage, true);
    }
  };

  const editAssessment = async (id, assessmentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedAssessment = await updateAssessment(id, assessmentData, authHeaders);
      setAssessments(prevAssessments =>
        prevAssessments.map(assessment => (assessment.id === id ? updatedAssessment : assessment))
      );
      showNotification('Assessment updated successfully!', false);
      return updatedAssessment;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating assessment.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeAssessment = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteAssessment(id, authHeaders);
      setAssessments(prevAssessments => prevAssessments.filter(assessment => assessment.id !== id));
      showNotification('Assessment removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing assessment.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllAssessments();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <AssessmentContext.Provider
      value={{
        assessments,
        selectedAssessment,
        fetchAllAssessments,
        fetchAssessmentById,
        addAssessment,
        editAssessment,
        removeAssessment,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => useContext(AssessmentContext);
